/**
 * Nokuhiro — CRO-Optimized Landing Page
 * Goal: Maximize App Store / Google Play clicks from cold parent traffic.
 * Structure follows the parent decision journey:
 * Pain → Clarity → Product Demo → Child Motivation → Parent Value → Trust → CTA
 */

import { useEffect, useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { InstagramFeed } from "@/components/InstagramFeed";

// ─── CONSTANTS ───
const APP_STORE_URL = "https://apps.apple.com/ch/app/nokuhiro/id6767195506";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.nokuhiro.myapp";

const ASSETS = {
  brandLogo: "/manus-storage/nokuhiro-brand-logo_7fbde860_5e938bb1.webp",
  iconClean: "/manus-storage/nokuhiro-icon-clean_02953123_0fec403f.webp",
  fullBody: "/manus-storage/nokuhiro-full-body_fa702dcd_a8291c5a.webp",
  forestBg1: "/manus-storage/forest-bg-1_aede169c.webp",
  forestBg2: "/manus-storage/noku-forest-bg-2_59eb356b_f87c396b.webp",
  forestBg3: "/manus-storage/noku-forest-bg-3_01faec7a_f90c3639.webp",
  forestBg4: "/manus-storage/noku-forest-bg-4_ec40c27a_7f17a064.webp",
  forestBg5: "/manus-storage/noku-forest-bg-5_ffe4a276_c66d1edb.webp",
  appStoreBadge: "/manus-storage/app-store-badge_e105c3c9.svg",
  googlePlayBadge: "/manus-storage/google-play-badge-cropped_7a9040a4.webp",
  screenQuests: "/manus-storage/noku-03-home-quests-screen_3ad3af38_7b55f630.webp",
  screenMap: "/manus-storage/noku-04-quest-map_f57c771b_214c7e27.webp",
  screenProfile: "/manus-storage/noku-05-hero-profile_85a27b54_0198469e.webp",
  screenParent: "/manus-storage/noku-06-parent-dashboard_134dac7e.webp",
  screenEdit: "/manus-storage/noku-08-edit-habits_ddda94a5_f6bf4447.webp",
  companionFox: "/manus-storage/noku-companion-fox_169a7cfe_47c0a05e.webp",
  companionPanda: "/manus-storage/noku-companion-panda_71b05802_55ec4727.webp",
  companionUnicorn: "/manus-storage/noku-companion-unicorn_2b76f300_b9a691b3.webp",
  companionLion: "/manus-storage/noku-companion-lion_b24cb2aa_bc2af8b0.webp",
  companionOwl: "/manus-storage/noku-companion-owl_6a260d1f_4547f851.webp",
  companionRabbit: "/manus-storage/noku-companion-rabbit_df00cdfc_79c203cc.webp",
};

// ─── TRACKING HELPER ───
function trackEvent(eventName: string, params?: Record<string, string>) {
  // Fire aggregate outbound store click event
  if (eventName.includes("app_store_click") || eventName.includes("google_play_click")) {
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "outbound_store_click_total", { source: eventName });
    }
    if (typeof window !== "undefined" && (window as any).fbq) {
      (window as any).fbq("trackCustom", "outbound_store_click_total", { source: eventName });
    }
  }
  // Google Ads / GA4
  if (typeof window !== "undefined" && (window as any).gtag) {
    (window as any).gtag("event", eventName, params);
  }
  // Meta Pixel
  if (typeof window !== "undefined" && (window as any).fbq) {
    (window as any).fbq("trackCustom", eventName, params);
  }
}

// ─── HOOKS ───
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, isInView };
}

// ─── NAVBAR ───
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const links = [
    { label: t.howItWorksCro.label || "How It Works", href: "#how-it-works" },
    { label: t.parentValue.label || "For Parents", href: "#for-parents" },
    { label: t.privacySafety.label || "Privacy", href: "#privacy" },
    { label: t.faqCro.label || "FAQ", href: "#faq" },
  ];

  return (
    <nav
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#1A3A2A]/95 backdrop-blur-md shadow-lg shadow-black/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href="#" className="flex items-center gap-3 group">
            <img
              src={ASSETS.iconClean}
              alt="Nokuhiro"
              width={48}
              height={48}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-none shadow-none group-hover:scale-105 transition-transform"
            />
            <span
              className="text-xl sm:text-2xl text-[#F5A623] tracking-wide hidden sm:block"
              style={{ fontFamily: "'Fredoka One', sans-serif" }}
            >
              Nokuhiro
            </span>
          </a>

          <div className="hidden md:flex items-center gap-1">
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="px-4 py-2 text-[#FFF8E7] hover:text-[#F5A623] transition-colors text-sm font-semibold tracking-wide"
                style={{ fontFamily: "'Nunito', sans-serif" }}
              >
                {link.label}
              </a>
            ))}
            <LanguageSwitcher />
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("nav_app_store_click")}
              className="ml-4 golden-btn text-sm !py-2 !px-6"
            >
              {t.heroCro.ctaIos}
            </a>
          </div>

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden text-[#F5A623] p-2"
            aria-label="Toggle menu"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              {menuOpen ? (
                <path d="M6 6l12 12M6 18L18 6" />
              ) : (
                <path d="M3 6h18M3 12h18M3 18h18" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-[#1A3A2A]/98 backdrop-blur-md border-t border-[#F5A623]/20">
          <div className="px-4 py-4 space-y-1">
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="block px-4 py-3 text-[#FFF8E7] hover:text-[#F5A623] hover:bg-[#2D4A3A]/50 rounded-lg transition-all font-semibold"
                style={{ fontFamily: "'Nunito', sans-serif" }}
              >
                {link.label}
              </a>
            ))}
            <div className="px-4 py-3">
              <LanguageSwitcher />
            </div>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => { setMenuOpen(false); trackEvent("nav_app_store_click"); }}
              className="block mt-3 golden-btn text-center text-sm !py-3"
            >
              {t.heroCro.ctaIos}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}

// ─── 1. HERO SECTION ───
function HeroSection() {
  const { t } = useLanguage();
  return (
    <section
      className="relative min-h-[90vh] flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `url(${ASSETS.forestBg1})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A3A2A]/70 via-[#1A3A2A]/50 to-[#1A3A2A]/80" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24 pb-16">
        <div>
          {/* App screenshots mockup */}
          <div className="flex justify-center items-end gap-3 sm:gap-5 mb-8">
            <div className="w-20 sm:w-28 md:w-36 rounded-2xl overflow-hidden shadow-xl border-2 border-[#F5A623]/40 -rotate-6 translate-y-4" style={{ aspectRatio: '390/844' }}>
              <img src={ASSETS.screenEdit} alt="Parent creates routine" width={390} height={844} className="w-full h-full object-cover" loading="eager" />
            </div>
            <div className="w-28 sm:w-36 md:w-44 rounded-2xl overflow-hidden shadow-2xl border-2 border-[#F5A623] z-10" style={{ aspectRatio: '390/844' }}>
              <img src={ASSETS.screenQuests} alt="Child follows quest" width={390} height={844} className="w-full h-full object-cover" loading="eager" fetchPriority="high" />
            </div>
            <div className="w-20 sm:w-28 md:w-36 rounded-2xl overflow-hidden shadow-xl border-2 border-[#F5A623]/40 rotate-6 translate-y-4" style={{ aspectRatio: '390/844' }}>
              <img src={ASSETS.screenProfile} alt="Progress and badges" width={390} height={844} className="w-full h-full object-cover" loading="eager" />
            </div>
          </div>

          <h1
            className="text-3xl sm:text-4xl md:text-6xl text-[#FFF8E7] mb-6 leading-tight"
            style={{ fontFamily: "'Fredoka One', sans-serif", textShadow: "0 3px 20px rgba(0,0,0,0.5)" }}
          >
            {t.heroCro.heading}
          </h1>

          <p
            className="text-[#FFF8E7]/90 text-base sm:text-lg md:text-xl max-w-3xl mx-auto mb-8 leading-relaxed"
            style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 500 }}
          >
            {t.heroCro.subheading}
          </p>

          {/* Direct store CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("hero_app_store_click")}
              className="inline-block transition-transform hover:scale-105"
            >
              <img src={ASSETS.appStoreBadge} alt="Download on the App Store" width={156} height={52} className="h-[44px] sm:h-[52px] w-auto" />
            </a>
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("hero_google_play_click")}
              className="inline-block transition-transform hover:scale-105"
            >
              <img src={ASSETS.googlePlayBadge} alt="Get it on Google Play" width={175} height={52} className="h-[44px] sm:h-[52px] w-auto" />
            </a>
          </div>

          {/* Trust microcopy */}
          <p
            className="text-[#FFF8E7]/70 text-sm font-semibold"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            {t.heroCro.trustLine}
          </p>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 80L60 72C120 64 240 48 360 44C480 40 600 48 720 52C840 56 960 56 1080 52C1200 48 1320 40 1380 36L1440 32V80H0Z" fill="#FFF8E7"/>
        </svg>
      </div>
    </section>
  );
}

// ─── 2. TRUST BAR ───
function TrustBar() {
  const { t } = useLanguage();
  const items = [
    { icon: "👶", text: t.trustBar.ages },
    { icon: "💰", text: t.trustBar.oneTime },
    { icon: "🚫", text: t.trustBar.noAds },
    { icon: "⚡", text: t.trustBar.quickRoutines },
  ];

  return (
    <section className="bg-[#FFF8E7] py-6 border-b border-[#F5A623]/20">
      <div className="max-w-5xl mx-auto px-4 flex flex-wrap justify-center gap-6 sm:gap-10">
        {items.map((item) => (
          <div key={item.text} className="flex items-center gap-2 text-[#1A3A2A]">
            <span className="text-lg">{item.icon}</span>
            <span className="text-sm font-bold" style={{ fontFamily: "'Nunito', sans-serif" }}>{item.text}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── 3. PARENT PAIN (Before/After) ───
function ParentPainSection() {
  const { ref, isInView } = useInView();
  const { t } = useLanguage();

  return (
    <section ref={ref} className="bg-[#FFF8E7] py-16 sm:py-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-[#C0392B] text-xs uppercase tracking-widest mb-3 font-bold" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {t.parentPain.label}
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#1A3A2A] leading-tight" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
            {t.parentPain.heading}
          </h2>
        </div>

        <div className={`grid md:grid-cols-2 gap-6 transition-all duration-700 delay-200 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Before */}
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 sm:p-8">
            <h3 className="text-lg font-bold text-[#E74C3C] mb-4 flex items-center gap-2" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
              <span className="text-2xl">😓</span> {t.parentPain.beforeTitle}
            </h3>
            <ul className="space-y-3">
              {t.parentPain.before.map((item) => (
                <li key={item} className="flex items-start gap-3 text-[#2D4A3A]" style={{ fontFamily: "'Nunito', sans-serif" }}>
                  <span className="text-red-400 mt-1">✗</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* After */}
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 sm:p-8">
            <h3 className="text-lg font-bold text-[#7ED957] mb-4 flex items-center gap-2" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
              <span className="text-2xl">✨</span> {t.parentPain.afterTitle}
            </h3>
            <ul className="space-y-3">
              {t.parentPain.after.map((item) => (
                <li key={item} className="flex items-start gap-3 text-[#2D4A3A]" style={{ fontFamily: "'Nunito', sans-serif" }}>
                  <span className="text-[#7ED957] mt-1">✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── 4. HOW IT WORKS (3 Steps) ───
function HowItWorksSection() {
  const { ref, isInView } = useInView();
  const { t } = useLanguage();
  const screenshots = [ASSETS.screenEdit, ASSETS.screenQuests, ASSETS.screenProfile];

  return (
    <section id="how-it-works" ref={ref} className="relative py-16 sm:py-24 overflow-hidden" style={{ backgroundImage: `url(${ASSETS.forestBg3})`, backgroundSize: "cover", backgroundPosition: "center", backgroundColor: "#1A3A2A" }}>
      <div className="absolute inset-0 bg-[#1A3A2A]/80" />
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-14 transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-[#F5A623] text-xs uppercase tracking-widest mb-3 font-bold" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {t.howItWorksCro.label}
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#FFF8E7]" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
            {t.howItWorksCro.heading}
          </h2>
        </div>

        <div className="grid sm:grid-cols-3 gap-6 sm:gap-8">
          {t.howItWorksCro.steps.map((step, i) => (
            <div
              key={step.title}
              className={`bg-[#FFF8E7] rounded-2xl p-6 text-center transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div className="w-48 mx-auto mb-4 rounded-xl overflow-hidden shadow-lg border-2 border-[#F5A623]/30" style={{ aspectRatio: '390/844' }}>
                <img src={screenshots[i]} alt={step.title} width={390} height={844} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-[#F5A623] flex items-center justify-center text-[#1A3A2A] text-lg font-bold" style={{ fontFamily: "'Bangers', cursive" }}>
                {step.icon}
              </div>
              <h3 className="text-lg text-[#1A3A2A] mb-2" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
                {step.title}
              </h3>
              <p className="text-[#2D4A3A] text-sm leading-relaxed" style={{ fontFamily: "'Nunito', sans-serif" }}>
                {step.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 5. PRODUCT CLARITY ───
function ProductClaritySection() {
  const { ref, isInView } = useInView();
  const { t } = useLanguage();
  const screens = [ASSETS.screenEdit, ASSETS.screenQuests, ASSETS.screenMap, ASSETS.screenProfile, ASSETS.screenParent];

  return (
    <section ref={ref} className="bg-[#FFF8E7] py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-[#8B5E00] text-xs uppercase tracking-widest mb-3 font-bold" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {t.productClarity.label}
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#1A3A2A] mb-3" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
            {t.productClarity.heading}
          </h2>
          <p className="text-[#2D4A3A] text-base max-w-2xl mx-auto" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {t.productClarity.subtitle}
          </p>
        </div>

        <div className={`flex overflow-x-auto gap-4 pb-4 snap-x snap-mandatory scrollbar-hide transition-all duration-700 delay-200 ${isInView ? "opacity-100" : "opacity-0"}`} onScroll={() => trackEvent("screenshot_carousel_interaction")}>
          {t.productClarity.features.map((feature, i) => (
            <div key={feature.title} className="flex-shrink-0 w-56 sm:w-64 snap-center">
              <div className="rounded-xl overflow-hidden shadow-lg border-2 border-[#F5A623]/20 mb-3" style={{ aspectRatio: '390/844' }}>
                <img src={screens[i]} alt={feature.title} width={390} height={844} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <h3 className="text-sm font-bold text-[#1A3A2A] mb-1" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
                {feature.title}
              </h3>
              <p className="text-xs text-[#2D4A3A]" style={{ fontFamily: "'Nunito', sans-serif" }}>
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 6. CHILD MOTIVATION ───
function ChildMotivationSection() {
  const { ref, isInView } = useInView();
  const { t } = useLanguage();

  return (
    <section ref={ref} className="relative py-16 sm:py-24 overflow-hidden" style={{ backgroundImage: `url(${ASSETS.forestBg5})`, backgroundSize: "cover", backgroundPosition: "center", backgroundColor: "#1A3A2A" }}>
      <div className="absolute inset-0 bg-[#1A3A2A]/80" />
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-[#F5A623] text-xs uppercase tracking-widest mb-3 font-bold" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {t.childMotivation.label}
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#FFF8E7] mb-4" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
            {t.childMotivation.heading}
          </h2>
          <p className="text-[#FFF8E7]/80 text-base max-w-3xl mx-auto" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {t.childMotivation.subtitle}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {t.childMotivation.items.map((item, i) => (
            <div
              key={item.title}
              className={`bg-[#FFF8E7]/10 backdrop-blur-sm border border-[#F5A623]/20 rounded-xl p-5 transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <span className="text-2xl mb-2 block">{item.icon}</span>
              <h3 className="text-[#FFF8E7] text-base font-bold mb-1" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
                {item.title}
              </h3>
              <p className="text-[#FFF8E7]/70 text-sm" style={{ fontFamily: "'Nunito', sans-serif" }}>
                {item.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Companion showcase */}
        <div className={`mt-12 flex justify-center gap-4 transition-all duration-700 delay-500 ${isInView ? "opacity-100" : "opacity-0"}`}>
          {[ASSETS.companionLion, ASSETS.companionFox, ASSETS.companionPanda, ASSETS.companionUnicorn, ASSETS.companionOwl].map((src, i) => (
            <div key={i} className="w-14 h-14 sm:w-18 sm:h-18 rounded-full overflow-hidden border-2 border-[#F5A623]/50 shadow-lg">
              <img src={src} alt="Companion" width={200} height={200} className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 7. PARENT VALUE ───
function ParentValueSection() {
  const { ref, isInView } = useInView();
  const { t } = useLanguage();

  return (
    <section id="for-parents" ref={ref} className="bg-[#FFF8E7] py-16 sm:py-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-[#8B5E00] text-xs uppercase tracking-widest mb-3 font-bold" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {t.parentValue.label}
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#1A3A2A]" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
            {t.parentValue.heading}
          </h2>
        </div>

        <div className={`grid sm:grid-cols-2 gap-4 mb-10 transition-all duration-700 delay-200 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {t.parentValue.benefits.map((benefit) => (
            <div key={benefit} className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm border border-[#F5A623]/10">
              <svg className="w-5 h-5 text-[#7ED957] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-[#1A3A2A] font-semibold text-sm" style={{ fontFamily: "'Nunito', sans-serif" }}>{benefit}</span>
            </div>
          ))}
        </div>


      </div>
    </section>
  );
}

// ─── 8. PAID VALUE JUSTIFICATION ───
function PaidValueSection() {
  const { ref, isInView } = useInView();
  const { t } = useLanguage();
  const tracked = useRef(false);
  useEffect(() => { if (isInView && !tracked.current) { tracked.current = true; trackEvent("pricing_section_view"); } }, [isInView]);
  return (
    <section ref={ref} className="relative py-16 sm:py-24 overflow-hidden" style={{ backgroundImage: `url(${ASSETS.forestBg4})`, backgroundSize: "cover", backgroundPosition: "center", backgroundColor: "#1A3A2A" }}>
      <div className="absolute inset-0 bg-[#1A3A2A]/80" />
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className={`transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-[#F5A623] text-xs uppercase tracking-widest mb-3 font-bold" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {t.paidValue.label}
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#FFF8E7] mb-6" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
            {t.paidValue.heading}
          </h2>
          <p className="text-[#FFF8E7]/80 text-base sm:text-lg max-w-2xl mx-auto mb-8" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {t.paidValue.body}
          </p>

          <div className="grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto mb-10 text-left">
            {t.paidValue.points.map((point) => (
              <div key={point} className="flex items-start gap-2 text-[#FFF8E7]/90">
                <svg className="w-5 h-5 text-[#F5A623] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm" style={{ fontFamily: "'Nunito', sans-serif" }}>{point}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("pricing_app_store_click")}
              className="inline-block transition-transform hover:scale-105"
            >
              <img src={ASSETS.appStoreBadge} alt="Download on the App Store" width={132} height={44} className="h-[44px] w-auto" />
            </a>
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("pricing_google_play_click")}
              className="inline-block transition-transform hover:scale-105"
            >
              <img src={ASSETS.googlePlayBadge} alt="Get it on Google Play" width={148} height={44} className="h-[44px] w-auto" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── 9. USE CASES ───
function UseCasesSection() {
  const { ref, isInView } = useInView();
  const { t } = useLanguage();

  return (
    <section ref={ref} className="bg-[#FFF8E7] py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-[#8B5E00] text-xs uppercase tracking-widest mb-3 font-bold" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {t.useCases.label}
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#1A3A2A]" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
            {t.useCases.heading}
          </h2>
        </div>

        <div className={`grid sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-all duration-700 delay-200 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {t.useCases.items.map((item) => (
            <div key={item.title} className="bg-white rounded-xl p-5 shadow-sm border border-[#F5A623]/10 hover:shadow-md transition-shadow">
              <h3 className="text-base font-bold text-[#1A3A2A] mb-3" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
                {item.title}
              </h3>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <span className="text-red-400 text-xs mt-1">✗</span>
                  <p className="text-xs text-[#2D4A3A] line-through" style={{ fontFamily: "'Nunito', sans-serif" }}>{item.from}</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-[#7ED957] text-xs mt-1">✓</span>
                  <p className="text-xs text-[#2D4A3A] font-semibold" style={{ fontFamily: "'Nunito', sans-serif" }}>{item.to}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 10. PRIVACY & SAFETY ───
function PrivacySafetySection() {
  const { ref, isInView } = useInView();
  const { t } = useLanguage();
  const tracked = useRef(false);
  useEffect(() => { if (isInView && !tracked.current) { tracked.current = true; trackEvent("privacy_section_view"); } }, [isInView]);
  return (
    <section id="privacy" ref={ref} className="relative py-16 sm:py-24 overflow-hidden" style={{ backgroundImage: `url(${ASSETS.forestBg2})`, backgroundSize: "cover", backgroundPosition: "center", backgroundColor: "#1A3A2A" }}>
      <div className="absolute inset-0 bg-[#1A3A2A]/85" />
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-[#F5A623] text-xs uppercase tracking-widest mb-3 font-bold" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {t.privacySafety.label}
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#FFF8E7]" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
            {t.privacySafety.heading}
          </h2>
        </div>

        <div className={`grid sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-all duration-700 delay-200 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {t.privacySafety.items.map((item, i) => (
            <div
              key={item.title}
              className="bg-[#FFF8E7]/10 backdrop-blur-sm border border-[#F5A623]/20 rounded-xl p-5"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <span className="text-2xl mb-2 block">{item.icon}</span>
              <h3 className="text-[#FFF8E7] text-sm font-bold mb-1" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
                {item.title}
              </h3>
              <p className="text-[#FFF8E7]/70 text-xs" style={{ fontFamily: "'Nunito', sans-serif" }}>
                {item.desc}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <a href="/privacy" className="inline-flex items-center gap-2 text-[#8B5E00] hover:text-[#6B4A00] text-sm font-bold transition-colors" style={{ fontFamily: "'Nunito', sans-serif" }}>
            Read Full Privacy Policy →
          </a>
        </div>
      </div>
    </section>
  );
}

// ─── 11. FOUNDER STORY ───
function FounderSection() {
  const { ref, isInView } = useInView();
  const { t } = useLanguage();

  return (
    <section ref={ref} className="bg-[#FFF8E7] py-16 sm:py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className={`transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-[#8B5E00] text-xs uppercase tracking-widest mb-3 font-bold" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {t.founder.label}
          </p>
          <h2 className="text-2xl sm:text-3xl text-[#1A3A2A] mb-6" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
            {t.founder.heading}
          </h2>
          <p className="text-[#2D4A3A] text-base sm:text-lg leading-relaxed" style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 500 }}>
            {t.founder.body}
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── 11b. PROOF / SOCIAL PROOF ───
function ProofSection() {
  const { ref, isInView } = useInView();
  const { t } = useLanguage();
  return (
    <section ref={ref} className="bg-[#FFF8E7] py-16 sm:py-24 border-t border-[#F5A623]/10">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-[#8B5E00] text-xs uppercase tracking-widest mb-3 font-bold" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {t.proof.label}
          </p>
          <h2 className="text-2xl sm:text-3xl text-[#1A3A2A]" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
            {t.proof.heading}
          </h2>
        </div>
        <div className={`grid md:grid-cols-2 gap-6 transition-all duration-700 delay-200 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="bg-white rounded-xl p-6 border border-[#F5A623]/10 shadow-sm">
            <div className="flex items-center gap-1 mb-3">
              {[1,2,3,4,5].map(s => (
                <svg key={s} className="w-4 h-4 text-[#F5A623]" fill="currentColor" viewBox="0 0 20 20"><path d="M10 1l2.39 4.84L17.5 6.7l-3.75 3.66.89 5.14L10 13.15l-4.64 2.35.89-5.14L2.5 6.7l5.11-.86L10 1z"/></svg>
              ))}
            </div>
            <p className="text-[#2D4A3A] text-sm italic leading-relaxed" style={{ fontFamily: "'Nunito', sans-serif" }}>
              "{t.proof.quote1}"
            </p>
            <p className="mt-3 text-[#2D4A3A] text-xs font-semibold" style={{ fontFamily: "'Nunito', sans-serif" }}>
              — {t.proof.quote1Attribution}
            </p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-[#F5A623]/10 shadow-sm">
            <div className="flex items-center gap-1 mb-3">
              {[1,2,3,4,5].map(s => (
                <svg key={s} className="w-4 h-4 text-[#F5A623]" fill="currentColor" viewBox="0 0 20 20"><path d="M10 1l2.39 4.84L17.5 6.7l-3.75 3.66.89 5.14L10 13.15l-4.64 2.35.89-5.14L2.5 6.7l5.11-.86L10 1z"/></svg>
              ))}
            </div>
            <p className="text-[#2D4A3A] text-sm italic leading-relaxed" style={{ fontFamily: "'Nunito', sans-serif" }}>
              "{t.proof.quote2}"
            </p>
            <p className="mt-3 text-[#2D4A3A] text-xs font-semibold" style={{ fontFamily: "'Nunito', sans-serif" }}>
              — {t.proof.quote2Attribution}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
// ─── 12. FAQ ───
function FaqSection() {
  const { ref, isInView } = useInView();
  const { t } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" ref={ref} className="bg-[#FFF8E7] py-16 sm:py-24 border-t border-[#F5A623]/10">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-[#8B5E00] text-xs uppercase tracking-widest mb-3 font-bold" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {t.faqCro.label}
          </p>
          <h2 className="text-2xl sm:text-3xl text-[#1A3A2A]" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
            {t.faqCro.heading}
          </h2>
        </div>

        <div className="space-y-3">
          {t.faqCro.items.map((item, i) => (
            <div
              key={i}
              className={`bg-white rounded-xl border border-[#F5A623]/10 overflow-hidden transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              <button
                onClick={() => { setOpenIndex(openIndex === i ? null : i); trackEvent("faq_expand", { question: item.q }); }}
                className="w-full px-5 py-4 text-left flex items-center justify-between gap-4"
                aria-expanded={openIndex === i}
              >
                <span className="text-[#1A3A2A] text-sm font-bold" style={{ fontFamily: "'Nunito', sans-serif" }}>{item.q}</span>
                <svg className={`w-5 h-5 text-[#F5A623] flex-shrink-0 transition-transform ${openIndex === i ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openIndex === i && (
                <div className="px-5 pb-4">
                  <p className="text-[#2D4A3A] text-sm leading-relaxed" style={{ fontFamily: "'Nunito', sans-serif" }}>{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 13. FINAL CTA ───
function FinalCtaSection() {
  const { ref, isInView } = useInView();
  const { t } = useLanguage();

  return (
    <section ref={ref} className="relative py-16 sm:py-24 overflow-hidden" style={{ backgroundImage: `url(${ASSETS.forestBg1})`, backgroundSize: "cover", backgroundPosition: "center" }}>
      <div className="absolute inset-0 bg-[#1A3A2A]/75" />
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className={`transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <img src={ASSETS.brandLogo} alt="Nokuhiro" width={80} height={80} className="w-20 h-20 mx-auto mb-6" loading="lazy" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-[#FFF8E7] mb-4" style={{ fontFamily: "'Fredoka One', sans-serif" }}>
            {t.finalCta.heading}
          </h2>
          <p className="text-[#FFF8E7]/70 text-sm mb-8" style={{ fontFamily: "'Nunito', sans-serif" }}>
            {t.heroCro.trustLine}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("final_app_store_click")}
              className="inline-block transition-transform hover:scale-105"
            >
              <img src={ASSETS.appStoreBadge} alt="Download on the App Store" width={156} height={52} className="h-[52px] w-auto" />
            </a>
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("final_google_play_click")}
              className="inline-block transition-transform hover:scale-105"
            >
              <img src={ASSETS.googlePlayBadge} alt="Get it on Google Play" width={175} height={52} className="h-[52px] w-auto" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── 14. FOOTER ───
function Footer() {
  const { t } = useLanguage();

  return (
    <footer className="bg-[#1A3A2A] text-[#FFF8E7] py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={ASSETS.iconClean} alt="Nokuhiro" width={40} height={40} className="w-10 h-10" />
              <span className="text-xl text-[#F5A623]" style={{ fontFamily: "'Fredoka One', sans-serif" }}>Nokuhiro</span>
            </div>
            <p className="text-[#FFF8E7]/75 text-sm" style={{ fontFamily: "'Nunito', sans-serif" }}>
              {t.footer.desc}
            </p>
          </div>

          <div>
            <h3 className="text-[#F5A623] text-sm font-bold mb-3" style={{ fontFamily: "'Fredoka One', sans-serif" }}>{t.footer.product}</h3>
            <ul className="space-y-2">
              <li><a href="#how-it-works" className="text-[#FFF8E7]/75 hover:text-[#F5A623] text-sm transition-colors" style={{ fontFamily: "'Nunito', sans-serif" }}>How It Works</a></li>
              <li><a href="#for-parents" className="text-[#FFF8E7]/75 hover:text-[#F5A623] text-sm transition-colors" style={{ fontFamily: "'Nunito', sans-serif" }}>For Parents</a></li>
              <li><a href="#faq" className="text-[#FFF8E7]/75 hover:text-[#F5A623] text-sm transition-colors" style={{ fontFamily: "'Nunito', sans-serif" }}>FAQ</a></li>
              <li><a href="/blog" className="text-[#FFF8E7]/75 hover:text-[#F5A623] text-sm transition-colors" style={{ fontFamily: "'Nunito', sans-serif" }}>Blog</a></li>
            </ul>
          </div>

          <div>
            <h3 className="text-[#F5A623] text-sm font-bold mb-3" style={{ fontFamily: "'Fredoka One', sans-serif" }}>{t.footer.support}</h3>
            <ul className="space-y-2">
              {t.footer.supportLinks.map((link) => (
                <li key={link.label}><a href={link.href} className="text-[#FFF8E7]/75 hover:text-[#F5A623] text-sm transition-colors" style={{ fontFamily: "'Nunito', sans-serif" }}>{link.label}</a></li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[#F5A623] text-sm font-bold mb-3" style={{ fontFamily: "'Fredoka One', sans-serif" }}>{t.footer.followUs}</h3>
            <a
              href="https://www.instagram.com/nokuhiro/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#FFF8E7]/75 hover:text-[#F5A623] text-sm transition-colors"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              @nokuhiro
            </a>
          </div>
        </div>

        <div className="border-t border-[#FFF8E7]/10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-[#FFF8E7]/70 text-xs" style={{ fontFamily: "'Nunito', sans-serif" }}>
            © {new Date().getFullYear()} {t.footer.copyright}
          </p>
          <div className="flex gap-4 text-[#FFF8E7]/70 text-xs" style={{ fontFamily: "'Nunito', sans-serif" }}>
            <span>{t.footer.coppa}</span>
            <span>{t.footer.gdpr}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── 15. STICKY MOBILE CTA ───
function StickyMobileCta() {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handler = () => {
      const scrollPercent = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      setVisible(scrollPercent > 0.2);
    };
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  if (!visible || dismissed) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-[#1A3A2A]/95 backdrop-blur-md border-t border-[#F5A623]/30 px-4 py-3 shadow-2xl">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[#FFF8E7] text-xs font-bold flex-shrink-0" style={{ fontFamily: "'Nunito', sans-serif" }}>
          {t.sticky.label}
        </span>
        <div className="flex gap-2">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("sticky_app_store_click")}
            className="bg-[#F5A623] text-[#1A3A2A] px-3 py-1.5 rounded-full text-xs font-bold"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            {t.sticky.ctaIos}
          </a>
          <a
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("sticky_google_play_click")}
            className="bg-[#7ED957] text-[#1A3A2A] px-3 py-1.5 rounded-full text-xs font-bold"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            {t.sticky.ctaAndroid}
          </a>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-[#FFF8E7]/75 hover:text-[#FFF8E7] p-1"
          aria-label="Close"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─── SCROLL TRACKING ───
function ScrollTracker() {
  useEffect(() => {
    const thresholds = [25, 50, 75];
    const fired = new Set<number>();

    const handler = () => {
      const scrollPercent = Math.round((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100);
      thresholds.forEach((t) => {
        if (scrollPercent >= t && !fired.has(t)) {
          fired.add(t);
          trackEvent(`scroll_${t}`);
        }
      });
    };

    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return null;
}

// ─── MAIN EXPORT ───
export default function Home() {
  return (
    <>
      <Navbar />
      <HeroSection />
      <TrustBar />
      <ParentPainSection />
      <HowItWorksSection />
      <ProductClaritySection />
      <ChildMotivationSection />
      <ParentValueSection />
      <PaidValueSection />
      <UseCasesSection />
      <PrivacySafetySection />
      <FounderSection />
      <ProofSection />
      <FaqSection />
      <InstagramFeed />
      <FinalCtaSection />
      <Footer />
      <StickyMobileCta />
      <ScrollTracker />
    </>
  );
}
